/**
 * src/components/spatial/chatShortcuts.ts
 *
 * The fast way to ask an object what can be done here.
 *
 * Typing "what can I do here?" and waiting for a model to decide to
 * call a tool works, but it is the slowest possible route to an answer
 * the server can produce directly — and a visitor on a phone, standing
 * in front of a building, is the person least able to type it. So the
 * question becomes a chip, and the chip reads the same service the
 * model's `get_nearby_actions` tool reads. One source of truth, two
 * ways in.
 *
 * Three rules shape everything here.
 *
 * **Explaining is not doing.** A chip describes an action and says
 * what it would need. Nothing in this file can execute anything, and
 * the descriptors it reads carry no arguments anybody could execute
 * with — a real action still goes through the interaction panel, its
 * authorization and a wallet confirmation. A shortcut that quietly
 * signed something would be the worst possible reading of the word.
 *
 * **The answer names its own limits.** The radius that was searched is
 * stated, because "nothing nearby" is only true of a stated distance.
 * So is whose "here" it is: a visitor who has shared no location gets
 * an answer about the building, and it says so rather than implying it
 * knows where they are standing.
 *
 * **The explanation is the one we already had.** `explainAction` in
 * `eventManagementModel` puts an action in the order a person asks
 * about it — action, then availability, then condition, then function,
 * with identifiers last. The descriptor this consumes is the same one
 * it was written for, so the ordering is reused rather than reinvented
 * in slightly different words.
 */

import type {
  NearbyAction,
  NearbyActionsAnswer,
  NearbyAgentsAnswer,
  ValueFlowAnswer,
} from '../../services/spatial/spatialPresenceClient';
import { explainAction, hasTechnicalDetail } from './eventManagementModel';

/** Whose surroundings the answer is about. */
export type ShortcutOrigin = 'visitor' | 'object';

export interface ChatShortcut {
  id: string;
  /** What the chip says. Short: it sits in a row on a phone. */
  label: string;
  /** The question it stands for, shown as the visitor's line. */
  question: string;
  kind: 'overview' | 'action' | 'agents' | 'deposits';
  /** Present on action chips. The descriptor, never an execution. */
  action?: NearbyAction;
  /** True when taking this action for real would need a signature. */
  requiresSigning: boolean;
}

/**
 * How many action chips are worth offering.
 *
 * A row of chips that needs scrolling is a row nobody reads, and the
 * server already ranked these by proximity and eligibility — so the
 * first few are the ones somebody standing here would want.
 */
export const SHORTCUT_LIMIT = 4;

function _metres(value: number | null | undefined): string {
  if (!Number.isFinite(value as number)) return 'nearby';
  const m = Math.round(value as number);
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${m} m`;
}

/** "of you" or "of the building", so no answer implies a fix we lack. */
function _origin(origin: ShortcutOrigin, objectName: string): string {
  return origin === 'visitor' ? 'you' : objectName;
}

/**
 * What the visitor was asking when they tapped an action chip.
 *
 * Phrased as the request rather than as a question about the control. A
 * read-about chip is somebody asking about the place, so it says so, using
 * the object's own name when the descriptor carried one.
 */
function _questionFor(action: NearbyAction): string {
  const label = action.label || action.verb || 'this';
  if (action.readOnly && action.verb === 'info') {
    const name = action.object?.name;
    return name ? `Tell me about ${name}.` : 'Tell me about this place.';
  }
  return `What does "${label}" do here?`;
}

/**
 * The chips to offer for an answer.
 *
 * The overview is always offered, including when nothing was found:
 * "nothing within 250 m" is an answer, and an empty row would read as
 * a surface that had not loaded.
 */
export function shortcutsFor(input: {
  answer: NearbyActionsAnswer | null | undefined;
  limit?: number;
  /** Offer the "who else is here?" chip. Set when a country is known. */
  agents?: boolean;
  /** Offer the deposit chip. Set when the object has a treasury to describe. */
  deposits?: boolean;
}): ChatShortcut[] {
  const limit = Number.isInteger(input.limit) ? (input.limit as number) : SHORTCUT_LIMIT;
  const chips: ChatShortcut[] = [{
    id: 'overview',
    label: 'What can I do here?',
    question: 'What can I do here?',
    kind: 'overview',
    requiresSigning: false,
  }];

  // Before the per-action chips, because both answer questions a visitor
  // asks early and neither depends on the action list having loaded.
  if (input.agents) {
    chips.push({
      id: 'agents',
      label: 'Who else is here?',
      question: 'Are there any AI agents around?',
      kind: 'agents',
      requiresSigning: false,
    });
  }
  if (input.deposits) {
    chips.push({
      id: 'deposits',
      label: 'What happens if I deposit?',
      question: 'What happens if I deposit here?',
      kind: 'deposits',
      requiresSigning: false,
    });
  }

  const actions = Array.isArray(input.answer?.actions) ? input.answer!.actions : [];
  for (const action of actions.slice(0, Math.max(0, limit))) {
    if (!action?.id) continue;
    chips.push({
      id: action.id,
      label: action.label || action.verb || 'Action',
      // The visitor's line, and it says what they wanted rather than naming
      // the control they pressed. `What is "Read about this"?` read as a
      // question about the button; somebody tapping it is asking about the
      // building.
      question: _questionFor(action),
      kind: 'action',
      action,
      requiresSigning: !!action.requiresSigning,
    });
  }

  return chips;
}

/**
 * The overview, in the object's own reply.
 *
 * Counts before names: somebody wants to know whether there is
 * anything here before they want the list. Blocked actions are
 * counted rather than hidden, because "three things, one of which
 * needs you to be closer" is the useful version of the answer.
 */
export function overviewText(input: {
  answer: NearbyActionsAnswer | null | undefined;
  objectName: string;
  origin: ShortcutOrigin;
}): string {
  const answer = input.answer;
  const where = _origin(input.origin, input.objectName);
  if (!answer) return 'I could not find out what is available here.';

  const radius = _metres(answer.searchedRadiusMeters);
  const actions = Array.isArray(answer.actions) ? answer.actions : [];

  // Said whatever the count is: a partial answer read as a complete
  // one is how "there is nothing here" becomes a lie.
  const partial: string[] = [];
  if (answer.sources?.objects && answer.sources.objects !== 'ok') partial.push('objects');
  if (answer.sources?.contractRules && answer.sources.contractRules !== 'ok') {
    partial.push('contract rules');
  }
  const caveat = partial.length
    ? `\nI could not reach ${partial.join(' or ')} just now, so there may be more.`
    : '';

  if (actions.length === 0) {
    return `Nothing to do within ${radius} of ${where} right now.${caveat}`;
  }

  const ready = actions.filter((a) => a?.eligibility?.passing);
  const blocked = actions.filter((a) => !a?.eligibility?.passing);

  // Some of these can sit outside the radius searched. A rule covers
  // the area it was drawn for, so one centred ten kilometres away
  // still reaches a visitor standing inside it, and it is returned
  // from wherever its centre happens to be. Saying "within 1 km" over
  // a list containing something four kilometres off would be false in
  // the one direction that matters, so the sentence says which it is.
  const searched = answer.searchedRadiusMeters;
  const beyond = typeof searched === 'number' && searched > 0
    ? actions.filter((a) => typeof a?.distanceMeters === 'number' && a.distanceMeters > searched)
    : [];

  const lines: string[] = [];
  lines.push(
    `Within ${radius} of ${where}${beyond.length
      ? `, plus ${beyond.length} whose own area reaches here`
      : ''}: ${ready.length} ${ready.length === 1 ? 'thing' : 'things'}`
    + ` you can do now${blocked.length ? `, ${blocked.length} that need something first` : ''}.`,
  );
  for (const action of actions.slice(0, SHORTCUT_LIMIT)) {
    const label = action.label || action.verb || 'Action';
    const mark = action.eligibility?.passing ? '•' : '–';
    // Distance only on the ones the heading does not already cover.
    // Printing it on everything would bury the exception in noise.
    const far = beyond.includes(action) ? `, ${_metres(action.distanceMeters)} away` : '';
    lines.push(`${mark} ${label}${action.requiresSigning ? ' (you sign)' : ''}${far}`);
  }
  if (actions.length > SHORTCUT_LIMIT) {
    lines.push(`…and ${actions.length - SHORTCUT_LIMIT} more.`);
  }

  return lines.join('\n') + caveat;
}

/**
 * One action, explained.
 *
 * The order is `explainAction`'s: what it is, whether it is available,
 * what it is conditional on, what it calls, and the identifiers last.
 * The closing line is the important one — it says where a real
 * execution happens, so nothing about this reads as having started
 * one.
 */
export function explanationText(
  action: NearbyAction | null | undefined,
  options: { echoLabel?: boolean } = {},
): string {
  const explained = explainAction(action);
  if (!explained) return 'I have nothing to say about that one.';

  const lines: string[] = [];
  // `explained.action` is `action.label`, which is the text printed on the
  // chip. Repeating it as the first line of the answer is how tapping
  // "Read about this" produced the words "Read about this" — the control
  // echoing its own name back and then speaking it aloud. A caller that
  // already showed the label suppresses it; `eventManagementModel`'s other
  // consumers, which render an explanation without a chip beside it, keep it.
  if (options.echoLabel !== false) lines.push(explained.action);
  lines.push(explained.availability.reason);

  // What the object says about itself, when the action is reading about it.
  //
  // "Read about this" used to print the two lines above and stop, which for
  // a read-only verb with no conditions is an answer that the action is
  // available and nothing about the place. A visitor who taps a chip
  // labelled "read about this" is asking for the content, so the content
  // goes here — above the machinery, which is what they asked about least.
  if (action?.readOnly && action.object?.summary) {
    lines.push(action.object.summary);
  } else if (action?.readOnly && action.verb === 'info') {
    // Said rather than padded. An object with no description is a thing an
    // operator can fix, and implying there was nothing to tell would hide
    // that.
    lines.push('There is nothing written about it yet.');
  }

  if (explained.conditions.temporal) lines.push(explained.conditions.temporal);
  if (explained.conditions.spatial) lines.push(explained.conditions.spatial);

  if (explained.functionName) lines.push(`Calls ${explained.functionName}()`);

  if (explained.signing.required) {
    // Never "I will do this for you". The panel and the wallet are
    // where it happens, and saying so is the whole point of a
    // read-only shortcut.
    lines.push(`Needs your signature — ${explained.signing.cost.toLowerCase()}.`);
    lines.push('Open it from the object panel when you want to go ahead.');
  }

  if (hasTechnicalDetail(explained)) {
    const t = explained.technical;
    const bits = [
      t.contractName,
      t.contractAddress,
      t.network,
      t.executionRuleId ? `rule ${t.executionRuleId}` : null,
    ].filter(Boolean);
    if (bits.length) lines.push(bits.join(' · '));
  }

  return lines.join('\n');
}

/**
 * Who else is standing here.
 *
 * The one rule: a search that did not run may not be reported as a search
 * that found nobody. A visitor cannot tell those apart from the wording, so
 * the wording has to do it — which is why a refusal produces "I could not
 * tell" and never "there is nobody".
 */
export function agentsText(input: {
  answer: NearbyAgentsAnswer | null | undefined;
  objectName: string;
  origin: ShortcutOrigin;
}): string {
  const answer = input.answer;
  const where = _origin(input.origin, input.objectName);
  if (!answer) {
    return 'I could not check for AI agents just now, so I cannot say whether any are nearby.';
  }

  const radius = _metres(answer.searchedRadiusMeters);
  const agents = Array.isArray(answer.agents) ? answer.agents : [];
  if (agents.length === 0) {
    return `No AI agent is moving within ${radius} of ${where} right now.`;
  }

  const lines = [
    `${agents.length === 1 ? 'One AI agent' : `${agents.length} AI agents`}`
    + ` within ${radius} of ${where}:`,
  ];
  for (const agent of agents) {
    const who = agent.displayName || 'an unnamed agent';
    const how = Number.isFinite(agent.distanceMeters as number)
      ? ` — ${_metres(agent.distanceMeters)} away`
      : '';
    // Worth stating: a passkey account behaves differently enough that
    // somebody debugging their own agent wants to know which they are
    // looking at.
    const kind = agent.addressKind === 'smart_account' ? ' (smart account)' : '';
    lines.push(`• ${who}${kind}${how}`);
  }

  const unlisted = answer.totalWithinRadius - agents.length;
  if (unlisted > 0) lines.push(`…and ${unlisted} more.`);

  return lines.join('\n');
}

/**
 * What happens to money deposited here.
 *
 * Written from the contract's own guarantees rather than from a sentence
 * somebody typed, because the two destinations behave oppositely and this
 * is the shortcut most able to cost a visitor money. Depositing into the
 * object is a gift the depositor cannot reverse; putting the same amount in
 * the country vault leaves it theirs. Both halves are always stated, so
 * nobody chooses the irreversible one without having been told.
 *
 * The one thing it must not do is imply a personal share of an object's
 * treasury. There is no such record on chain — `perDepositorRecord` is
 * false — so the object's balance is described as the object's.
 */
export function depositsText(input: {
  answer: ValueFlowAnswer | null | undefined;
  objectName: string;
}): string {
  const answer = input.answer;
  if (!answer) return 'I could not check what happens to a deposit here just now.';

  const name = input.objectName;
  const asset = answer.object.balance?.asset?.code || 'XLM';
  const lines: string[] = [];

  lines.push(`${name} holds ${answer.object.balance.amount} ${asset} in its treasury.`);
  // The object's, not anybody's share of it.
  if (answer.guarantees.object.perDepositorRecord === false) {
    lines.push(
      'That is the total it holds — deposits are not tracked per person, '
      + 'so there is no individual share to look up.',
    );
  }

  if (!answer.object.canReceive) {
    lines.push(`It cannot accept a deposit at the moment${
      answer.object.blocker ? ` (${answer.object.blocker.replace(/_/g, ' ')})` : ''}.`);
  } else {
    const g = answer.guarantees.object;
    lines.push(
      `If you deposit into ${name}, it becomes the object's: ${
        g.depositorCanWithdraw ? 'you could withdraw it' : 'you cannot withdraw it back'
      }, and only the beneficiary named for it can collect.`,
    );
    if (g.feeOnDeposit) lines.push("A country fee comes off the amount before it's credited.");
    if (answer.object.unclaimable) {
      // Money can arrive with nobody able to collect it. Not lost — a
      // beneficiary can be named later — but not funding anything today.
      lines.push('Nobody is named to collect from it yet.');
    }
  }

  const vault = answer.countryVault;
  const g = answer.guarantees.countryVault;
  if (g.depositorCanWithdraw) {
    lines.push(
      `A deposit into the ${vault.country || 'country'} vault instead stays yours`
      + ' — you can withdraw that one, subject to the country\'s checks.',
    );
  }

  if (vault.balance && vault.balance.ok) {
    lines.push(`Your ${vault.country || 'country'} vault balance: ${
      vault.balance.amount} ${vault.balance.asset?.code || asset}.`);
  } else if (vault.balance && !vault.balance.ok) {
    // Never "you have nothing".
    lines.push('I could not read your vault balance just now.');
  } else if (!vault.walletConnected) {
    lines.push('Connect a wallet and I can tell you your own vault balance.');
  }

  return lines.join('\n');
}

/**
 * Whether an answer is worth keeping chips on screen for.
 *
 * A refusal or an empty answer still gets the overview chip, so this
 * is only about the per-action ones.
 */
export function hasActionShortcuts(answer: NearbyActionsAnswer | null | undefined): boolean {
  return Array.isArray(answer?.actions) && answer!.actions.length > 0;
}

// ── Resolving a chip to what it was asking for ────────────────────────────

/**
 * What tapping a chip produces.
 *
 * Either words we already have, or a question to put to the object. The
 * second case exists because some chips ask for content the client does not
 * hold, and the honest options are to ask or to admit there is no answer —
 * not to print the chip's own label and call it a reply.
 */
export type ShortcutResolution =
  /** Answered locally. Nothing was asked of any model. */
  | { kind: 'text'; text: string }
  /** Needs the object. Goes through the ordinary message path. */
  | { kind: 'ask'; question: string };

/**
 * Turn a tapped chip into the thing it was standing in for.
 *
 * THE BUG THIS FIXES. A chip is a prompt, not an answer. "Read about this"
 * is what a visitor taps when they want to hear about the building; it is
 * not itself a sentence the building should say. The old path ran every
 * action chip through `explanationText`, whose first line was the action's
 * label, so the reply to "Read about this" began with the words "Read about
 * this" — and with no description on the object, that plus an availability
 * line was the entire answer. Then it was synthesised, so the building read
 * the name of the button back to the visitor.
 *
 * THE RULE. A chip resolves to content or it resolves to a question. It
 * never resolves to its own label.
 *
 * WHERE THE MODEL COMES IN. Almost nowhere, deliberately. The overview,
 * agents and deposits chips are answered entirely from data the card already
 * fetched — the same services the object's own tools read — so tapping them
 * costs nothing and returns instantly. Only one case asks: a read-about chip
 * for an object whose description the nearby-actions feed did not carry. The
 * server holds that text in `spatialContextGatherer`, so asking is how the
 * visitor gets it; the alternative is answering a request for content with
 * "there is nothing written about it yet" while the description sits one
 * call away.
 */
export function resolveShortcut(input: {
  shortcut: ChatShortcut;
  answer?: NearbyActionsAnswer | null;
  agents?: NearbyAgentsAnswer | null;
  valueFlow?: ValueFlowAnswer | null;
  objectName: string;
  origin: ShortcutOrigin;
}): ShortcutResolution {
  const { shortcut, objectName, origin } = input;

  switch (shortcut.kind) {
    case 'overview':
      return {
        kind: 'text',
        text: overviewText({ answer: input.answer ?? null, objectName, origin }),
      };
    case 'agents':
      return {
        kind: 'text',
        text: agentsText({ answer: input.agents ?? null, objectName, origin }),
      };
    case 'deposits':
      return {
        kind: 'text',
        text: depositsText({ answer: input.valueFlow ?? null, objectName }),
      };
    default:
      return _resolveAction(shortcut.action, objectName);
  }
}

/**
 * An action chip, resolved to content rather than to its name.
 *
 * Two shapes of answer, decided by what is actually known:
 *
 * A READ-ABOUT chip is a request for the object's own words. When the
 * descriptor carried them, they are the answer and they lead — above the
 * machinery, which is what the visitor asked about least. When it did not,
 * the object is asked, because it has the text and we do not.
 *
 * ANY OTHER action chip is a request to understand what the action would do.
 * `explainAction` produces that — availability, conditions, the function it
 * calls, what signing costs — and with the label suppressed it reads as a
 * description instead of an echo. But an action whose explanation is nothing
 * but an availability line has told the visitor only that a button works,
 * which is the same empty answer in a different costume, so that case asks
 * too.
 */
function _resolveAction(
  action: NearbyAction | null | undefined,
  objectName: string,
): ShortcutResolution {
  if (!action) return { kind: 'text', text: 'I have nothing to say about that one.' };

  const readingAbout = !!action.readOnly && action.verb === 'info';
  const summary = action.object?.summary;

  if (readingAbout) {
    if (!summary) {
      // The one model call in this file, and the reason it is here: the
      // server's context gatherer holds the description this descriptor
      // did not carry.
      return { kind: 'ask', question: `Tell me about ${objectName}.` };
    }
    return { kind: 'text', text: explanationText(action, { echoLabel: false }) };
  }

  const text = explanationText(action, { echoLabel: false });
  // With the label gone, a bare availability line is all that is left for an
  // action with no conditions, no contract and no signing. "Available now" on
  // its own is not an answer to "what is this".
  const substantive = text.split('\n').filter((line) => line.trim()).length > 1;
  if (!substantive) {
    const label = action.label || action.verb || 'this';
    return { kind: 'ask', question: `What does "${label}" do here?` };
  }

  return { kind: 'text', text };
}
